// Location messages in a thread: the card a pin, meeting point or live ETA
// shows (a mini map rendered lazily, "Open in Maps"), the pinned message's
// banner, the strip for your own live ETA (Stop) and the "Share location"
// sheet beside the composer. Pure helpers: chat/location.ts.
import type { Schemas } from "@10twenty/api";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, unwrap } from "../api";
import { excerpt, isExpired, summaryText, type Conversation, type Message } from "../chat/chat";
import {
  defaultMeetAt,
  etaToPinned,
  etaView,
  LABEL_MAX,
  MapSlots,
  mapsPlatform,
  mapsUrl,
  meetAtFromInput,
  meetAtInput,
  meetAtText,
  ownLiveEta,
  pinnedLocation,
  placeName,
  shareBody,
  SnapshotCache,
  type LocationCreate,
  type MessageLocation,
  type ShareSource,
  type Spot,
} from "../chat/location";
import type { ChatStore } from "../chat/store";
import { pointFromPosition } from "../checkins/checkIns";
import { currentPosition, LocationUnavailable } from "../checkins/geolocation";
import { timeText } from "../history/history";
import { sortPlaces } from "../places/places";
import { distanceText } from "../units";
import { useUnits } from "../useUnits";

const MiniMap = lazy(() => import("../map/MiniMap"));
const PointMap = lazy(() => import("../map/PointMap"));

type Place = Schemas["Place"];

/** Mini maps rendering at once (each a WebGL context until it is copied to an image). */
const mapSlots = new MapSlots(3);
/** Rendered mini maps, so scrolling back or reopening a chat shows them at once. */
const snapshots = new SnapshotCache(60);
/** Start rendering a mini map this far before it scrolls into view. */
const NEAR_VIEW = "200px";

function usePlatform() {
  return useMemo(() => mapsPlatform(navigator.userAgent, navigator.maxTouchPoints), []);
}

/** Opens in a new tab, except `geo:` links, which hand over to an Android app. */
function MapsLink(props: { href: string; className?: string; children: React.ReactNode; label?: string }) {
  const external = !props.href.startsWith("geo:");
  return (
    <a
      href={props.href}
      className={props.className}
      aria-label={props.label}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {props.children}
    </a>
  );
}

/**
 * A still map of the point: an image once rendered, else a live map while it
 * is near the viewport and one of the few slots is free.
 */
function LocationMap(props: { point: Spot; href: string; label: string }) {
  const { point } = props;
  const ref = useRef<HTMLAnchorElement>(null);
  const [image, setImage] = useState(() => snapshots.get(point));
  const [near, setNear] = useState(false);
  const [live, setLive] = useState(false);
  /** The map never finished or couldn't be copied: the card shows the plain frame. */
  const [failed, setFailed] = useState(false);
  const release = useRef<(() => void) | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || image || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => setNear(entries.some((e) => e.isIntersecting)), {
      rootMargin: NEAR_VIEW,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [image]);

  useEffect(() => {
    if (!near || image || failed) return;
    release.current = mapSlots.request(() => setLive(true));
    return () => {
      release.current?.();
      release.current = null;
      setLive(false);
    };
  }, [near, image, failed]);

  function snapshot(url: string) {
    snapshots.set(point, url);
    setImage(url);
  }

  return (
    <MapsLink href={props.href} className="chat-minimap" label={`${props.label}: open in a maps app`}>
      <span ref={ref} className="chat-minimap-frame">
        {image ? (
          <img src={image} alt="" />
        ) : live ? (
          <Suspense fallback={null}>
            <MiniMap lat={point.lat} lon={point.lon} onSnapshot={snapshot} onFail={() => setFailed(true)} />
          </Suspense>
        ) : null}
        <span className="chat-minimap-pin" aria-hidden="true" />
        <span className="chat-minimap-credit">© OpenStreetMap</span>
      </span>
    </MapsLink>
  );
}

/** A location message's card. The sender's own position is never shown for an ETA. */
export function LocationCard(props: { message: Message; selfId: string; nowMs: number; onStop: () => void }) {
  const { message, nowMs } = props;
  const location = message.location;
  const units = useUnits();
  const platform = usePlatform();
  if (!location) return <span className="chat-unsent">This location is no longer shared.</span>;
  const name = placeName(location);
  const href = mapsUrl(location, location.label, platform);
  return (
    <div className={`chat-location ${location.type}`}>
      <LocationMap point={location} href={href} label={name} />
      <div className="chat-location-text">
        <LocationDetails location={location} own={message.sender_id === props.selfId} nowMs={nowMs} units={units}>
          {(live) =>
            live && message.sender_id === props.selfId ? (
              <button type="button" className="link-button" onClick={props.onStop}>
                Stop sharing
              </button>
            ) : null
          }
        </LocationDetails>
        <MapsLink href={href} className="chat-location-open">
          Open in Maps
        </MapsLink>
      </div>
    </div>
  );
}

function LocationDetails(props: {
  location: MessageLocation;
  own: boolean;
  nowMs: number;
  units: ReturnType<typeof useUnits>;
  children: (live: boolean) => React.ReactNode;
}) {
  const { location, nowMs, units } = props;
  switch (location.type) {
    case "pin":
      return (
        <>
          <strong>{placeName(location)}</strong>
          {location.address && <span className="hint">{location.address}</span>}
          {location.source === "current" && location.taken_at && (
            <span className="hint">
              {timeText(Date.parse(location.taken_at))}
              {location.accuracy_m !== null && ` · within ${distanceText(location.accuracy_m, units)}`}
            </span>
          )}
        </>
      );
    case "meeting_point":
      return (
        <>
          <span className="chat-location-kind">Meeting point</span>
          <strong>{location.label ?? "Meeting point"}</strong>
          {location.meet_at && <span>Meet {meetAtText(location.meet_at, nowMs)}</span>}
          {location.address && <span className="hint">{location.address}</span>}
        </>
      );
    case "eta": {
      const view = location.eta ? etaView(location.eta, nowMs, units) : null;
      return (
        <>
          <span className="chat-location-kind">
            {props.own ? "Your ETA" : "ETA"} to {placeName(location)}
          </span>
          <strong className={view?.live ? "chat-eta live" : "chat-eta"}>{view?.headline ?? "Live ETA"}</strong>
          {view?.detail && <span className="hint">{view.detail}</span>}
          {props.children(view?.live ?? false)}
        </>
      );
    }
  }
}

/** The pinned message over the thread: a meeting point (open, ETA there) or any other message. */
export function PinnedBanner(props: {
  conversation: Conversation;
  store: ChatStore;
  selfId: string;
  nowMs: number;
  /** Your own live ETA in this chat (a new one would stop it). */
  liveEta: Message | null;
  onShow: (seq: number) => void;
  onError: (error: unknown) => void;
}) {
  const { conversation, store, nowMs } = props;
  const platform = usePlatform();
  const pinned = conversation.pinned;
  if (!pinned || isExpired(pinned.message, nowMs)) return null;
  const located = pinnedLocation(conversation, nowMs);
  const message = pinned.message;

  function shareEta() {
    if (!located) return;
    const to = placeName(located.location);
    const stop = props.liveEta ? " This stops the ETA you're sharing now." : "";
    const question =
      `Share your live ETA to ${to} with this chat? It updates as you move until you arrive, ` +
      `stop it or 3 hours pass. Your position itself isn't shown.${stop}`;
    if (window.confirm(question)) store.sendLocation(conversation.id, etaToPinned(located.location));
  }

  return (
    <div className="chat-pinned" role="region" aria-label="Pinned message">
      <span className="chat-pinned-icon" aria-hidden="true">
        📌
      </span>
      <button type="button" className="chat-pinned-text" onClick={() => props.onShow(message.seq)}>
        {located ? (
          <>
            <strong>{located.location.type === "meeting_point" ? "Meeting point" : "Pinned"}</strong>{" "}
            {placeName(located.location)}
            {located.location.meet_at && ` · ${meetAtText(located.location.meet_at, nowMs)}`}
          </>
        ) : (
          <>
            <strong>Pinned</strong> {excerpt(summaryText(message))}
          </>
        )}
      </button>
      {located && (
        <MapsLink href={mapsUrl(located.location, located.location.label, platform)} className="link-button">
          Open in Maps
        </MapsLink>
      )}
      {located && conversation.writable && (
        <button type="button" className="link-button" onClick={shareEta}>
          Share ETA
        </button>
      )}
      <button
        type="button"
        className="link-button"
        onClick={() => {
          if (window.confirm("Unpin this message for everyone?")) store.unpin(conversation.id).catch(props.onError);
        }}
      >
        Unpin
      </button>
    </div>
  );
}

/** Above the composer while you share a live ETA here. */
export function EtaStrip(props: { messages: readonly Message[]; selfId: string; onStop: (message: Message) => void }) {
  const eta = ownLiveEta(props.messages, props.selfId);
  if (!eta?.location) return null;
  return (
    <p className="chat-eta-strip" role="status">
      <span className="chat-eta-dot" aria-hidden="true" />
      You're sharing your ETA to {placeName(eta.location)}.{" "}
      <button type="button" className="link-button" onClick={() => props.onStop(eta)}>
        Stop
      </button>
    </p>
  );
}

// ------------------------------------------------------------ share sheet

type Tab = "current" | "pin" | "places";
type Reading = { state: "idle" | "reading" } | { state: "error"; message: string };

/** Places this chat can send: its circle's, or in a DM those of circles both people are in. */
function usePlaces(conversation: Conversation, selfId: string, enabled: boolean) {
  const circleId = conversation.circle_id;
  const peerId = conversation.kind === "direct" ? conversation.peer_id : null;
  return useQuery({
    queryKey: circleId ? ["circle", circleId, "places"] : ["chat", "dm-places", peerId],
    enabled: enabled && (circleId !== null || peerId !== null),
    queryFn: async (): Promise<{ place: Place; circle: string | null }[]> => {
      if (circleId) {
        const places = unwrap(await api.GET("/circles/{circleId}/places", { params: { path: { circleId } } }));
        return places.map((place) => ({ place, circle: null }));
      }
      const circles = unwrap(await api.GET("/circles"));
      const lists = await Promise.all(
        circles.map(async (circle) => {
          const params = { params: { path: { circleId: circle.id } } };
          const members = unwrap(await api.GET("/circles/{circleId}/members", params));
          const both = members.some((m) => m.user_id === peerId) && members.some((m) => m.user_id === selfId);
          if (!both) return [];
          const places = unwrap(await api.GET("/circles/{circleId}/places", params));
          return places.map((place) => ({ place, circle: circle.name }));
        }),
      );
      return lists.flat();
    },
  });
}

export function ShareLocationSheet(props: {
  conversation: Conversation;
  store: ChatStore;
  selfId: string;
  nowMs: number;
  /** Your own live ETA in this chat (a new one stops it). */
  liveEta: Message | null;
  /** The message being replied to: a location sent from here answers it. */
  replyToId: string | null;
  /** A location was queued (the reply draft is used up). */
  onSent: () => void;
  onClose: () => void;
}) {
  const { conversation, store, nowMs } = props;
  const [tab, setTab] = useState<Tab>("current");
  const [reading, setReading] = useState<Reading>({ state: "reading" });
  const [current, setCurrent] = useState<Schemas["LiveLocation"] | null>(null);
  const [dropped, setDropped] = useState<Spot | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [type, setType] = useState<LocationCreate["type"]>("pin");
  const [label, setLabel] = useState("");
  const [timed, setTimed] = useState(false);
  const [meetAt, setMeetAt] = useState(() => meetAtInput(defaultMeetAt(Date.now())));
  const [pinIt, setPinIt] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const places = usePlaces(conversation, props.selfId, true);
  const pinned = pinnedLocation(conversation, nowMs);
  const sorted = useMemo(() => {
    const list = places.data ?? [];
    const byId = new Map(list.map((p) => [p.place.id, p]));
    return sortPlaces(list.map((p) => p.place)).map((place) => byId.get(place.id)!);
  }, [places.data]);
  const place = sorted.find((p) => p.place.id === placeId)?.place ?? null;
  const reads = useRef(0);

  /** One reading each time "My location" is chosen (also on opening the sheet); the newest one counts. */
  const read = useCallback(() => {
    const at = ++reads.current;
    currentPosition().then(
      (position) => {
        if (at !== reads.current) return;
        setCurrent(pointFromPosition(position));
        setReading({ state: "idle" });
      },
      (err: unknown) => {
        if (at !== reads.current) return;
        const message = err instanceof LocationUnavailable ? err.message : "Your location isn't available.";
        setReading({ state: "error", message });
      },
    );
  }, []);
  useEffect(read, [read]);

  // My location can't be an ETA's destination.
  const kind = tab === "current" && type === "eta" ? "pin" : type;
  const source: ShareSource | null =
    tab === "current"
      ? current && reading.state === "idle"
        ? { kind: "current", point: current }
        : null
      : tab === "pin"
        ? dropped && { kind: "dropped", lat: dropped.lat, lon: dropped.lon }
        : place && { kind: "place", placeId: place.id, name: place.name };

  function choose(next: Tab) {
    setError(null);
    setTab(next);
    if (next === "current" && tab !== "current") {
      // A new reading replaces the last: an older one is never sent.
      setCurrent(null);
      setReading({ state: "reading" });
      read();
    }
  }

  function send(body: LocationCreate, pin: boolean) {
    store.sendLocation(conversation.id, body, { pin, replyToId: props.replyToId });
    props.onSent();
    props.onClose();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!source) return;
    const time = kind === "meeting_point" && timed ? meetAtFromInput(meetAt) : null;
    if (kind === "meeting_point" && timed && time === null) {
      setError("Pick a date and time.");
      return;
    }
    const result = shareBody(source, kind, label, time, Date.now());
    if ("error" in result) {
      setError(result.error);
      return;
    }
    send(result.body, kind === "meeting_point" && pinIt);
  }

  const center = pinned?.location ?? current ?? sorted[0]?.place ?? null;
  return (
    <div className="chat-share card" role="dialog" aria-label="Share a location">
      <div className="chat-share-head">
        <div className="chat-share-tabs" role="tablist" aria-label="Where">
          {(
            [
              ["current", "My location"],
              ["pin", "Drop a pin"],
              ["places", "Places"],
            ] as const
          ).map(([value, text]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? "active" : undefined}
              onClick={() => choose(value)}
            >
              {text}
            </button>
          ))}
        </div>
        <button type="button" className="chat-icon-button" aria-label="Close" onClick={props.onClose}>
          ×
        </button>
      </div>
      {pinned && conversation.writable && (
        <p className="chat-share-quick">
          <button
            type="button"
            className="link-button"
            onClick={() => send(etaToPinned(pinned.location), false)}
            title={props.liveEta ? "This stops the ETA you're sharing now." : undefined}
          >
            Share my ETA to {placeName(pinned.location)}
          </button>
        </p>
      )}
      <form onSubmit={submit}>
        {tab === "current" && (
          <div className="chat-share-body">
            {reading.state === "reading" && <p className="muted">Finding your location…</p>}
            {reading.state === "error" && (
              <p className="error" role="alert">
                {reading.message}{" "}
                <button type="button" className="link-button" onClick={() => choose("pin")}>
                  Drop a pin instead
                </button>
              </p>
            )}
            {current && reading.state === "idle" && (
              <>
                <Suspense fallback={<div className="point-map" />}>
                  <PointMap point={current} onPick={null} label="Your location" />
                </Suspense>
                <p className="hint">
                  Read just now, within {Math.round(current.accuracy_m)} m. It is sent once and stored only with the
                  message.
                </p>
              </>
            )}
          </div>
        )}
        {tab === "pin" && (
          <div className="chat-share-body">
            <Suspense fallback={<div className="point-map" />}>
              <PointMap
                point={dropped}
                onPick={(lat, lon) => setDropped({ lat, lon })}
                label="Drop a pin"
                center={center}
                pickCentre="Drop the pin at the map's centre"
              />
            </Suspense>
            <p className="hint">
              {dropped
                ? "Click elsewhere to move the pin."
                : "Click the map to drop a pin, or move the map and drop it at the centre."}
            </p>
          </div>
        )}
        {tab === "places" && (
          <div className="chat-share-body">
            {places.isPending ? (
              <p className="muted">Loading places…</p>
            ) : places.isError ? (
              <p className="error">Places couldn't be loaded.</p>
            ) : sorted.length === 0 ? (
              <p className="muted">
                {conversation.kind === "direct"
                  ? "No circle you share has places yet."
                  : "This circle has no places yet."}
              </p>
            ) : (
              <ul className="chat-share-places">
                {sorted.map(({ place: p, circle }) => (
                  <li key={p.id}>
                    <label>
                      <input
                        type="radio"
                        name="chat-share-place"
                        checked={placeId === p.id}
                        onChange={() => {
                          setPlaceId(p.id);
                          setError(null);
                        }}
                      />
                      <span>
                        <strong>{p.name}</strong>
                        {(p.address || circle) && (
                          <span className="hint"> {[p.address, circle].filter(Boolean).join(" · ")}</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {source && (
          <div className="chat-share-options">
            <fieldset className="chat-share-kinds">
              <legend className="visually-hidden">Send as</legend>
              {(
                [
                  ["pin", "Location"],
                  ["meeting_point", "Meet here"],
                  ...(tab === "current" ? [] : ([["eta", "Share my ETA to here"]] as const)),
                ] as const
              ).map(([value, text]) => (
                <label key={value}>
                  <input type="radio" name="chat-share-kind" checked={kind === value} onChange={() => setType(value)} />
                  {text}
                </label>
              ))}
            </fieldset>
            {tab !== "places" && (
              <label className="chat-share-field">
                {kind === "meeting_point" ? "Name" : "Name (optional)"}
                <input
                  type="text"
                  value={label}
                  maxLength={LABEL_MAX * 2}
                  placeholder={kind === "meeting_point" ? "Rundle Mall fountain" : ""}
                  onChange={(event) => setLabel(event.target.value)}
                />
              </label>
            )}
            {kind === "meeting_point" && (
              <>
                <label className="chat-share-check">
                  <input type="checkbox" checked={timed} onChange={(event) => setTimed(event.target.checked)} />
                  At a time
                </label>
                {timed && (
                  <label className="chat-share-field">
                    Meet at
                    <input
                      type="datetime-local"
                      value={meetAt}
                      min={meetAtInput(nowMs)}
                      onChange={(event) => setMeetAt(event.target.value)}
                    />
                  </label>
                )}
                <label className="chat-share-check">
                  <input type="checkbox" checked={pinIt} onChange={(event) => setPinIt(event.target.checked)} />
                  Pin it in this chat
                </label>
              </>
            )}
            {kind === "eta" && (
              <p className="hint">
                Your ETA updates as you move, until you arrive, stop it or 3 hours pass. Your position itself isn't
                shown.{props.liveEta && " This stops the ETA you're sharing now."}
              </p>
            )}
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <div className="button-row">
              <button type="submit">
                {kind === "eta" ? "Share ETA" : kind === "meeting_point" ? "Send meeting point" : "Send location"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

/** The composer's "Share location" button. */
export function ShareLocationButton(props: { open: boolean; onToggle: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="chat-icon-button chat-attach"
      aria-label="Share a location"
      aria-expanded={props.open}
      title="Share a location"
      disabled={props.disabled}
      onClick={props.onToggle}
    >
      📍
    </button>
  );
}
